import React, { useState, useEffect } from "react";
import { apiService, Campaign } from "../../services";
import CampaignFilters from "./CampaignFilters";
import CampaignHeader from "./CampaignHeader";
import CampaignTable from "./CampaignTable";
import CampaignPagination from "./CampaignPagination";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

interface CampaignManagementProps {
  onPageChange: (page: string) => void;
}

const CampaignManagement: React.FC<CampaignManagementProps> = ({
  onPageChange,
}) => {
  // State management
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDateFilter, setStartDateFilter] = useState<string>("");
  const [endDateFilter, setEndDateFilter] = useState<string>("");

  // Delete modal
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    campaign: Campaign | null;
  }>({
    isOpen: false,
    campaign: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.getCampaigns();
        setCampaigns(response.data);
      } catch (error: any) {
        console.error("Failed to fetch campaigns:", error);
        setError(error.message || "Không thể tải danh sách chiến dịch");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Filter campaigns, newest first
  const filteredCampaigns = campaigns
    .filter((campaign) => {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch =
        campaign.name.toLowerCase().includes(lowerSearch) ||
        (campaign.description || "").toLowerCase().includes(lowerSearch) ||
        (campaign.batch || "").toLowerCase().includes(lowerSearch) ||
        (campaign.product_name || "").toLowerCase().includes(lowerSearch);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && campaign.status === 1) ||
        (statusFilter === "inactive" && campaign.status === 2);
      const matchesType =
        typeFilter === "all" ||
        (campaign.type || "normal") === typeFilter;

      // Date range filter (inclusive): show campaigns overlapping range
      let matchesDate = true;
      const hasStart = Boolean(startDateFilter);
      const hasEnd = Boolean(endDateFilter);
      if (hasStart || hasEnd) {
        const campaignStart = new Date(campaign.start_date).setHours(0, 0, 0, 0);
        const campaignEnd = new Date(campaign.end_date).setHours(23, 59, 59, 999);
        const filterStart = hasStart
          ? new Date(startDateFilter).setHours(0, 0, 0, 0)
          : Number.NEGATIVE_INFINITY;
        const filterEnd = hasEnd
          ? new Date(endDateFilter).setHours(23, 59, 59, 999)
          : Number.POSITIVE_INFINITY;
        // Overlap condition inclusive by day
        matchesDate = campaignEnd >= filterStart && campaignStart <= filterEnd;
      }

      return matchesSearch && matchesStatus && matchesType && matchesDate;
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  // Get unique products for filter
  const uniqueProducts = Array.from(
    new Set(campaigns.map((c) => c.product_name))
  );

  // Pagination
  const totalPages = Math.ceil(filteredCampaigns.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentCampaigns = filteredCampaigns.slice(startIndex, endIndex);

  // Handlers - Gọi API để cập nhật trạng thái
  const handleToggleStatus = async (campaignId: string) => {
    const campaign = campaigns.find((c) => c._id === campaignId);
    if (!campaign) return;

    // Kiểm tra ngày kết thúc
    const now = new Date();
    const endDate = new Date(campaign.end_date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    const isExpired = endDay < today;

    // Nếu chiến dịch đã kết thúc và muốn bật (status = 1), yêu cầu chỉnh sửa ngày kết thúc
    if (isExpired && campaign.status === 2) {
      showToastMessage(
        "Chiến dịch đã kết thúc. Vui lòng chỉnh sửa ngày kết thúc trước khi bật chiến dịch.",
        "error"
      );
      return;
    }

    // Toggle status: 1 (bật) <-> 2 (tắt)
    // API accepts: 1 (bật), 2 (tắt)
    const newStatus = campaign.status === 1 ? 2 : 1;

    // Validate status value (newStatus can only be 1 or 2 from toggle logic)
    if (newStatus !== 1 && newStatus !== 2) {
      showToastMessage("Giá trị trạng thái không hợp lệ", "error");
      return;
    }

    console.log(
      `Updating campaign ${campaignId} status from ${campaign.status} to ${newStatus}`
    );
    console.log("Current campaign:", campaign);
    console.log("Toggle logic: campaign.status === 1 ? 2 : 1");
    console.log("Result:", campaign.status === 1 ? 2 : 1);
    console.log("API will be called with status:", newStatus);
    console.log("Expected behavior: status 1 = BẬT, status 2 = TẮT");

    try {
      // Gọi API để cập nhật trạng thái
      const response = await apiService.updateCampaignStatus(
        campaignId,
        newStatus
      );

      console.log("API Response:", response);
      console.log("Response success:", response.success);
      console.log("Response data:", response.data);
      console.log("Response message:", response.message);

      if (response.success && response.data) {
        // Cập nhật local state với dữ liệu từ API
        console.log("Updating local state with:", response.data);
        console.log("Response data status:", response.data.status);
        console.log(
          "Status interpretation:",
          response.data.status === 1 ? "BẬT" : "TẮT"
        );
        setCampaigns((prev) => {
          const updated = prev.map((c) =>
            c._id === campaignId ? response.data! : c
          );
          console.log("Updated campaigns:", updated);
          return updated;
        });
        showToastMessage(
          `Chiến dịch "${response.data.name}" đã được ${
            response.data.status === 1 ? "bật" : "tắt"
          } thành công!`,
          "success"
        );
      } else {
        console.error("API Error Response:", response);
        showToastMessage(
          response.message || "Không thể cập nhật trạng thái chiến dịch",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error updating campaign status:", error);
      showToastMessage(
        error.message || "Lỗi kết nối. Vui lòng thử lại!",
        "error"
      );
    }
  };

  const handleDelete = (campaign: Campaign) => {
    // Chỉ hiển thị modal xác nhận, không gọi API
    setDeleteModal({ isOpen: true, campaign });
  };

  const confirmDelete = async () => {
    if (!deleteModal.campaign) return;

    try {
      setIsDeleting(true);
      // Gọi API để xóa chiến dịch
      const response = await apiService.deleteCampaign(
        deleteModal.campaign._id!
      );

      // Cập nhật local state sau khi API thành công
      setCampaigns((prev) =>
        prev.filter((c) => c._id !== deleteModal.campaign!._id)
      );
      setDeleteModal({ isOpen: false, campaign: null });

      // Hiển thị thông báo từ API response
      showToastMessage(
        response.message || "Xóa chiến dịch thành công!",
        "success"
      );
    } catch (error: any) {
      console.error("Failed to delete campaign:", error);
      showToastMessage(error.message || "Không thể xóa chiến dịch", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleExportExcel = () => {
    // TODO: Implement export functionality
    console.log("Export to Excel");
  };

  const handleDownloadTemplate = () => {
    // TODO: Implement template download
    console.log("Download template");
  };

  const showToastMessage = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleCreateNew = () => {
    // Chuyển đến trang tạo chiến dịch
    onPageChange("campaigns/create");
  };

  const handleEditCampaign = (campaign: Campaign) => {
    // Chuyển đến trang chỉnh sửa chiến dịch
    onPageChange(`campaigns/edit/${campaign._id}`);
  };

  const handleShowDeleteModal = (campaign: Campaign) => {
    setDeleteModal({
      isOpen: true,
      campaign: campaign,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.campaign) {
      await confirmDelete();
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, campaign: null });
  };

  const handleDateFilterChange = (startDate: string, endDate: string) => {
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setProductFilter("all");
    setStartDateFilter("");
    setEndDateFilter("");
    setCurrentPage(1);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            Đang tải danh sách chiến dịch...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div>
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-500"
        >
          Thử lại
        </button>
      </div>
    );
  }

  // Show empty state when no campaigns
  if (campaigns.length === 0) {
    return (
      <div>
        <CampaignHeader onCreateNew={handleCreateNew} />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Không có chiến dịch nào
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Hiện tại chưa có chiến dịch nào trong hệ thống. Hãy tạo chiến dịch
              đầu tiên của bạn.
            </p>
            <button
              onClick={handleCreateNew}
              className="bg-blue-600 text-white px-4 py-3 rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
            >
              + Tạo chiến dịch mới
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CampaignHeader onCreateNew={handleCreateNew} />

      <CampaignFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        productFilter={productFilter}
        setProductFilter={setProductFilter}
        uniqueProducts={uniqueProducts}
        onExportExcel={handleExportExcel}
        onDownloadTemplate={handleDownloadTemplate}
        onClearFilters={handleClearFilters}
        onDateFilterChange={handleDateFilterChange}
      />

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <CampaignTable
          campaigns={currentCampaigns}
          onToggleStatus={handleToggleStatus}
          onEdit={handleEditCampaign}
          onDelete={handleDelete}
          onPageChange={onPageChange}
          onShowDeleteModal={handleShowDeleteModal}
        />

        <CampaignPagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={filteredCampaigns.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        campaign={deleteModal.campaign}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
      />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-md shadow-lg flex items-center ${
              toastType === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {toastType === "success" ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              )}
            </svg>
            {toastMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;
